import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import {
  Modal,
  Box,
  IconButton,
  Button,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import LanguageIcon from '@mui/icons-material/Language';
import { LanguageContext } from '../../context/LanguageContext';

// Function to save translations to localStorage
const saveTranslationsToStorage = (language, translations) => {
  try {
    localStorage.setItem(`translations_${language}`, JSON.stringify(translations));
  } catch (err) {
    console.error('Failed to save translations to localStorage:', err);
  }
};
// Function to load translations from localStorage
const loadTranslationsFromStorage = (language) => {
  try {
    const stored = localStorage.getItem(`translations_${language}`);
    return stored ? JSON.parse(stored) : null;
  } catch (err) {
    console.error('Failed to load translations from localStorage:', err);
    return null;
  }
};
const LocalizationModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { language } = useContext(LanguageContext);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const namespaces = [
    'translation',
    'vehicle',
    'schedule',
    'stop',
    'route',
    'user',
    'manageData',
    'alert'
  ];

  // Load saved translations on component mount
  React.useEffect(() => {
    const savedTranslations = loadTranslationsFromStorage(language);
    if (savedTranslations) {
      Object.entries(savedTranslations).forEach(([namespace, content]) => {
        i18next.addResourceBundle(language, namespace, content, true, true);
      });
      i18next.changeLanguage(language);
    }
  }, [language]);

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '100%',
    maxWidth: 500,
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: 2,
    p: 0,
    outline: 'none',
  };
  const headerStyle = {
    backgroundColor: 'primary.main',
    color: 'white',
    py: 2,
    px: 3,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    position: 'relative',
  };

  const contentStyle = {
    p: 3,
  };
  const handleDownloadKeys = () => {
    try {
      // Get all resources from all namespaces and combine them
      const allKeys = {};

      namespaces.forEach(namespace => {
        const resources = i18next.getResourceBundle(language, namespace);
        if (resources) {
          Object.keys(resources).forEach(key => {
            // Add namespace prefix to avoid key conflicts
            allKeys[`${namespace}:${key}`] = '';
          });
        }
      });

      // Create and download the file
      const blob = new Blob([JSON.stringify(allKeys, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${language}-all-keys.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download keys file');
    }
  };

  const parseFileContent = (content, fileType) => {
    try {
      switch (fileType) {
        case 'application/json':
          const translations = JSON.parse(content);
          const isConsolidated = Object.keys(translations).some(key =>
            ['translation', 'vehicle', 'schedule', 'stop', 'route',
              'user', 'manageData', 'alert'].includes(key)
          );

          if (isConsolidated) {
            return { isConsolidated: true, translations };
          } else {
            return { isConsolidated: false, translations };
          }

        case 'text/plain':
          return content.split('\n').reduce((acc, line) => {
            const [key, value] = line.split('=').map(str => str.trim());
            if (key && value) {
              acc[key] = value;
            }
            return acc;
          }, {});
        case 'text/xml':
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(content, 'text/xml');
          const entries = xmlDoc.getElementsByTagName('entry');
          return Array.from(entries).reduce((acc, entry) => {
            const key = entry.getAttribute('key');
            const value = entry.textContent;
            if (key && value) {
              acc[key] = value;
            }
            return acc;
          }, {});
        default:
          throw new Error('Unsupported file type');
      }
    } catch (err) {
      throw new Error('Failed to parse file content');
    }
  };

  // const handleFileUpload = async (event) => {
  //   const file = event.target.files[0];
  //   setError('');
  //   setSuccess(false);

  //   if (!file) return;

  //   try {
  //     const content = await file.text();
  //     const { isConsolidated, translations } = parseFileContent(content, file.type);

  //     if (isConsolidated) {
  //       Object.entries(translations).forEach(([namespace, content]) => {
  //         i18next.addResourceBundle(
  //           language,
  //           namespace,
  //           content,
  //           true,
  //           true
  //         );
  //       });
  //     } else {
  //       // Split keys by namespace prefix
  //       const namespacedTranslations = Object.entries(translations).reduce((acc, [key, value]) => {
  //         const [namespace, actualKey] = key.split(':');
  //         if (!acc[namespace]) acc[namespace] = {};
  //         acc[namespace][actualKey] = value;
  //         return acc;
  //       }, {});

  //       Object.entries(namespacedTranslations).forEach(([namespace, content]) => {
  //         i18next.addResourceBundle(
  //           language,
  //           namespace,
  //           content,
  //           true,
  //           true
  //         );
  //       });
  //     }
  //     i18next.changeLanguage(language);

  //     setSuccess(true);
  //     setTimeout(() => {
  //       onClose();
  //       setSuccess(false);
  //     }, 1500);
  //   } catch (err) {
  //     setError('Failed to process the file. Please check the format.');
  //   }

  //   event.target.value = '';
  // };


  // const handleFileUpload = async (event) => {
  //   const file = event.target.files[0];
  //   setError('');
  //   setSuccess(false);

  //   if (!file) return;

  //   try {
  //     const content = await file.text();
  //     const { isConsolidated, translations } = parseFileContent(content, file.type);

  //     let processedTranslations = {};

  //     if (isConsolidated) {
  //       processedTranslations = translations;
  //       Object.entries(translations).forEach(([namespace, content]) => {
  //         i18next.addResourceBundle(language, namespace, content, true, true);
  //       });
  //     } else {
  //       // Split keys by namespace prefix
  //       processedTranslations = Object.entries(translations).reduce((acc, [key, value]) => {
  //         const [namespace, actualKey] = key.split(':');
  //         if (!acc[namespace]) acc[namespace] = {};
  //         acc[namespace][actualKey] = value;
  //         return acc;
  //       }, {});

  //       Object.entries(processedTranslations).forEach(([namespace, content]) => {
  //         i18next.addResourceBundle(language, namespace, content, true, true);
  //       });
  //     }

  //     // Save translations to localStorage
  //     saveTranslationsToStorage(language, processedTranslations);

  //     i18next.changeLanguage(language);
  //     setSuccess(true);
  //     setTimeout(() => {
  //       onClose();
  //       setSuccess(false);
  //     }, 1500);
  //   } catch (err) {
  //     setError('Failed to process the file. Please check the format.');
  //   }

  //   event.target.value = '';
  // };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    setError('');
    setSuccess(false);

    if (!file) return;

    try {
      const content = await file.text();
      const { isConsolidated, translations } = parseFileContent(content, file.type);

      let processedTranslations = {};

      if (isConsolidated) {
        processedTranslations = translations;
        Object.entries(translations).forEach(([namespace, content]) => {
          // Add to 'ud' language instead of current language
          i18next.addResourceBundle('ud', namespace, content, true, true);
        });
      } else {
        // Split keys by namespace prefix
        processedTranslations = Object.entries(translations).reduce((acc, [key, value]) => {
          const [namespace, actualKey] = key.split(':');
          if (!acc[namespace]) acc[namespace] = {};
          acc[namespace][actualKey] = value;
          return acc;
        }, {});

        Object.entries(processedTranslations).forEach(([namespace, content]) => {
          // Add to 'ud' language instead of current language
          i18next.addResourceBundle('ud', namespace, content, true, true);
        });
      }

      // Save translations to localStorage with 'ud' language code
      saveTranslationsToStorage('ud', processedTranslations);

      // Don't change the current language
      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSuccess(false);
        window.location.reload()
      }, 1500);
    } catch (err) {
      setError('Failed to process the file. Please check the format.');
    }

    event.target.value = '';
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="upload-translations-modal"
      aria-describedby="modal-to-upload-translation-files"
    >
      <Paper sx={modalStyle}>
        {/* Header */}
        <Box sx={headerStyle}>
          <Typography variant="h6" component="h2" sx={{ mb: 0 }}>
            <FileUploadIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {t('Upload Translations')}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'white',
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={contentStyle}>
          {/* Supported Formats */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Supported formats:
          </Typography>
          <List dense sx={{ mb: 2 }}>
            <ListItem>
              <ListItemIcon>
                <DescriptionIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="JSON (.json)" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <TextSnippetIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Text file with key=value pairs (.txt)" />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          {/* Upload and Download Section */}
          <Box sx={{
            display: 'flex',
            gap: 2,
            mb: 2,
            '& .MuiButton-root': { minWidth: 'fit-content' }
          }}>
            <Button
              variant="contained"
              component="label"
              sx={{ flex: 1 }}
              startIcon={<FileUploadIcon />}
            >
              Select File
              <input
                type="file"
                accept=".json,.txt"
                onChange={handleFileUpload}
                hidden
              />
            </Button>

            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadKeys}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Download Keys
            </Button>
          </Box>

          {/* Status Messages */}
          {error && (
            <Typography
              color="error"
              variant="body2"
              sx={{
                mt: 2,
                p: 1,
                bgcolor: 'error.light',
                borderRadius: 1,
                opacity: 0.9
              }}
            >
              {error}
            </Typography>
          )}

          {success && (
            <Typography
              color="success"
              variant="body2"
              sx={{
                mt: 2,
                p: 1,
                bgcolor: 'success.light',
                borderRadius: 1,
                opacity: 0.9
              }}
            >
              Translations updated successfully!
            </Typography>
          )}
        </Box>
      </Paper>
    </Modal>
  );
};

export default LocalizationModal;